import moment from 'moment';
import { isLoaded } from 'react-redux-firebase';
import { createSelector } from 'reselect';
import {
  map,
  filter,
  size,
  toArray,
  orderBy,
  min,
  get,
  includes,
  values,
  compact,
  find,
  isEmpty,
  every,
  chain,
} from 'lodash';
import { formatPrice } from 'utils/formatPrice';
import {
  getJobRolesMarkedAsLead,
  getSignedLeadRoles,
  getDisplayFee,
} from 'utils/staffing/projects';
import {
  MEETING_PROVIDERS,
  PROJECT_LOCATION_TYPES_SELECT_OPTIONS,
  PROJECT_STATUS_STATES,
  DEAL_STATUS_STATES,
} from 'constants/staffing';
import { isDealInitializing } from 'utils/staffing/status';

const projectsSelector = (state, id) => state.firestore.data.projects;
const projectSelector = (state, id) => state.firestore.data.projects?.[id];
const jobSelector = (state, id, jobId) => state.firestore.data.projects?.[id]?.jobs[jobId];
const volunteerRoleSelector = (state, id, volunteerRoleId) =>
  state.firestore.data.projects?.[id]?.volunteerRoles?.[volunteerRoleId];
const volunteersSelector = (state, id) => state.firestore.data.projects?.[id]?.volunteers || {};
const contractorsSelector = (state, id) => state.firestore.data.contractors;
const projectMethodologiesSelector = state => state.firestore.data.projectMethodologies;
const dealsSelector = state => state.firestore.data.deals;
const staffingSelector = state => state.staffing;
const companiesSelector = state => state.firestore.data.companies;

/**
 * Selector for retrieving all Projects
 */
export const selectProjects = () => {
  return createSelector([projectsSelector, staffingSelector], (projects, staffing) => {
    if (!isLoaded(projects)) return { isLoaded: false };

    const filteredProjects = compact(
      map(projects, project => {
        return filterOutProjects(project, staffing);
      }),
    );

    return {
      projects: map(orderBy(filteredProjects, ['eventName'], ['desc']), project => {
        const isActive = checkProjectIsActive(project);
        const hasJobs = !isEmpty(project.jobs);

        return {
          ...project,
          isActive,
          hasJobs,
        };
      }),
      isLoaded: true,
    };
  });
};

/**
 * Selector for retrieving the Project info from the Redux Store
 */
export const selectProject = () => {
  return createSelector(
    [
      projectSelector,
      contractorsSelector,
      dealsSelector,
      projectMethodologiesSelector,
      companiesSelector,
    ],
    (project, contractors, deals, projectMethodologies, companies) => {
      if (!isLoaded(project) || !isLoaded(deals) || !isLoaded(projectMethodologies)) return null;

      const {
        startDate,
        endDate,
        jobs,
        startTime,
        endTime,
        locationType,
        timezone,
        startDateLocal,
        endDateLocal,
        startTimeLocal,
        endTimeLocal,
      } = project;

      const formattedStartDate = startDate ? moment(startDate).format('MM/DD/YYYY') : 'Not Set';
      const formattedStartDateLocal = startDateLocal
        ? moment(startDateLocal).format('MM/DD/YYYY')
        : 'Not Set';
      const formattedEndDate = endDate ? moment(endDate).format('MM/DD/YYYY') : 'Not Set';
      const formattedEndDateLocal = endDateLocal
        ? moment(endDateLocal).format('MM/DD/YYYY')
        : 'Not Set';

      const formattedStartTime =
        startDate && startTime ? moment(startTime, 'HH:mm').format('h:mm A') : 'Not Set';
      const formattedStartTimeLocal =
        startDateLocal && startTimeLocal
          ? moment(startTimeLocal, 'HH:mm').format('h:mm A')
          : 'Not Set';
      const formattedEndTime =
        endDate && endTime ? moment(endTime, 'HH:mm').format('h:mm A') : 'Not Set';
      const formattedEndTimeLocal =
        endDateLocal && endTimeLocal ? moment(endTimeLocal, 'HH:mm').format('h:mm A') : 'Not Set';

      const formattedTimezone = timezone ? moment.tz(timezone).format('z') : 'Not Set';

      const isActive = checkProjectIsActive(project);

      let numberOfFilledJobs = 0;
      let numberOfSignedJobs = 0;
      let numberOfPaidJobs = 0;
      let projectTotalCost = 0;
      let allLeadsHaveVCZoomLicenses = true;

      const deal = deals ? deals[project.dealId] : null;
      const projectMethodology = projectMethodologies
        ? projectMethodologies[project.projectMethodologyId]
        : null;

      //Get the display name for the Location Type
      const displayLocationType =
        find(PROJECT_LOCATION_TYPES_SELECT_OPTIONS, m => m.value === locationType)?.name || '';

      map(jobs, job => {
        //Get the Number of Jobs that all Seats have been accepted by Contractors
        const allSeatsAccepted =
          size(
            filter(values(job.assignees), a => includes(['accepted', 'signed', 'paid'], a.status)),
          ) === job.seats;
        if (allSeatsAccepted) {
          numberOfFilledJobs = numberOfFilledJobs + 1;
        }
        const allSeatsSigned =
          size(filter(values(job.assignees), a => includes(['signed', 'paid'], a.status))) ===
          job.seats;
        if (allSeatsSigned) {
          numberOfSignedJobs = numberOfSignedJobs + 1;
        }
        const allSeatsPaid =
          size(filter(values(job.assignees), a => includes(['paid'], a.status))) === job.seats;
        if (allSeatsPaid) {
          numberOfPaidJobs = numberOfPaidJobs + 1;
        }

        // Google Meet projects do not use VC Zoom licenses; omit check (matches Cloud Functions onUpdate).
        if (
          job.isLead &&
          (!project.meetingProvider || project.meetingProvider === MEETING_PROVIDERS.zoom)
        ) {
          allLeadsHaveVCZoomLicenses =
            allLeadsHaveVCZoomLicenses &&
            every(
              // make sure we are only checking valid assignees (some prior bugs left bad data)
              filter(job.assignees, assignee => assignee?.contractorId && assignee?.status),
              assignee => {
                return contractors[assignee.contractorId]?.hasVCZoomLicense;
              },
            );
        }

        //Get the Project Total Cost by Multiplying the Job Hourly Rate of every Assignee by the Max Hours
        map(job.assignees, assignee => {
          const contractor = contractors[assignee.contractorId];
          if (contractor) {
            projectTotalCost =
              projectTotalCost +
              (job.isFixedFee
                ? job.fixedRate
                : min([Number(job.hourlyRate), Number(contractor.hourlyRate)]) * job.maxHours); //If Job uses fix fees, use that value, if not, use the hourlyRate * maxHours to calculate the Project Total Cost
          }
        });
      });

      const checklistsCompleted = project.checklists
        ? filter(project.checklists, chklst => !chklst.completed).length === 0
        : true;

      return {
        ...project,
        checklistsCompleted,
        jobs: orderBy(jobs, ['startDate'], ['asc']),
        jobRolesAlreadyMarkedAsLead: getJobRolesMarkedAsLead(jobs),
        signedLeadRoles: getSignedLeadRoles(jobs),
        allLeadsHaveVCZoomLicenses,
        numberOfFilledJobs,
        numberOfSignedJobs,
        numberOfPaidJobs,
        projectTotalCost,
        isActive,
        formattedStartDate,
        formattedEndDate,
        formattedStartTime,
        formattedEndTime,
        formattedStartDateLocal,
        formattedEndDateLocal,
        formattedStartTimeLocal,
        formattedEndTimeLocal,
        formattedTimezone,
        displayLocationType,
        formattedProjectContractValue: formatPrice(project.contractValue),
        dealName: deal?.dealName || 'Not set',
        dealContactName: deal?.contactName || 'Not set',
        dealContactEmail: deal?.contactEmail || 'Not set',
        dealReferralFee: deal?.referralFee || 0,
        dealReferralFeeRecipient: deal?.referralFeeRecipient || '',
        dealReferralFeePaid: deal?.referralFeePaid || false,
        clientName:
          project.companyName ||
          (project.status === PROJECT_STATUS_STATES.INITIALIZING ? 'Initializing' : 'Not set'),
        projectMethodologyName: projectMethodology?.title || '',
      };
    },
  );
};

export const projectFiltersDerivedSelectOptions = () => {
  return createSelector([projectsSelector], projects => {
    if (!isLoaded(projects))
      return {
        venues: [],
      };

    const venueOptions = chain(projects)
      .map(project => {
        return project?.venue
          ? {
              name: project?.venue,
              value: project?.venue,
            }
          : null;
      })
      .compact()
      .uniqBy('name')
      .orderBy('name')
      .value();

    return {
      venues: venueOptions,
    };
  });
};

/**
 * Selector for retrieving the Job Info of a Project from the Redux Store
 */
export const selectJob = () => {
  return createSelector([jobSelector, contractorsSelector], (job, contractors) => {
    if (!job) return null;
    if (!isLoaded(contractors)) return null;

    //Get the Job Contractors Info from the Contractors store
    const jobContractors = [];
    job &&
      map(job.assignees, assignee => {
        const contractor = contractors[assignee?.contractorId ?? ''];
        if (contractor) {
          jobContractors.push({ ...contractor, ...assignee });
        }
      });

    const pendingInvitations = toArray(filter(jobContractors, { status: 'pending' }));
    const acceptedInvitations = toArray(
      filter(jobContractors, contractor => {
        return (
          contractor.status === 'accepted' ||
          contractor.status === 'signed' ||
          contractor.status === 'paid'
        );
      }),
    );
    const rejectedInvitations = toArray(filter(jobContractors, { status: 'rejected' }));

    // const unAssignedSeats = Number(job.seats) - size(acceptedInvitations) - size(rejectedInvitations);

    // const placeHolderSeats = fill(Array(unAssignedSeats), ""); //create an array of remaining seats

    return {
      ...job,
      displayFee: getDisplayFee(job),
      allContractors: jobContractors,
      // placeHolderSeats,
      pendingInvitations,
      acceptedInvitations,
      rejectedInvitations,
    };
  });
};

/**
 * Selector for retrieving a Volunteer Role of a Project from the Redux Store
 */
export const selectVolunteerRole = () => {
  return createSelector(
    [volunteerRoleSelector, volunteersSelector],
    (volunteerRole, volunteers) => {
      if (!volunteerRole) return null;

      volunteers = chain(volunteers)
        .map((volunteer, k) => {
          return {
            ...volunteer,
            id: k,
          };
        })
        .filter(volunteer => volunteer.roleId === volunteerRole.id)
        .value();

      return {
        ...volunteerRole,
        volunteers,
      };
    },
  );
};

/**
 * Checks if the Start Date has passed to know whether the project is active
 * @param {Object} project
 * @returns
 */
export const checkProjectIsActive = project => {
  return project && project.status !== PROJECT_STATUS_STATES.CLOSED;
};

export const selectProjectMethodologies = () => {
  return createSelector([projectMethodologiesSelector], projectMethodologies => {
    if (!isLoaded(projectMethodologies)) return { projectMethodologies: [], isLoaded: false };

    // react-redux-firebase keeps a deleted doc as `null` in `data.projectMethodologies[id]`
    // until the key is dropped. Skip those entries — reading `.title` / `.id` on null is what
    // crashed the Companies list after DELETE.
    const mapped = compact(
      map(projectMethodologies, (projectMethodology, methodologyId) => {
        if (!projectMethodology) {
          return null;
        }

        return {
          name: projectMethodology.title,
          value: projectMethodology.id || methodologyId,
        };
      }),
    );

    return {
      projectMethodologies: orderBy(mapped, ['name'], ['asc']),
      isLoaded: true,
    };
  });
};

export const selectDealsDropdown = () => {
  return createSelector([dealsSelector], deals => {
    if (!isLoaded(deals)) return { deals: [], isLoaded: false };

    // Exclude deals that cannot accept new projects: completed/closed, or still
    // initializing (dealFolder is created during init and is required by project create).
    deals = filter(deals, deal => {
      return (
        deal.status !== DEAL_STATUS_STATES.COMPLETED &&
        deal.status !== DEAL_STATUS_STATES.CLOSED &&
        !isDealInitializing(deal.status)
      );
    });

    return {
      deals: map(orderBy(deals, [sort => get(sort, 'dealName').toLowerCase()]), deal => {
        return {
          name: `${deal.dealName} (${deal.companyName})`,
          value: deal.id,
        };
      }),
      isLoaded: true,
    };
  });
};

/**
 *
 * @param {*} staffing
 * @param {*} project
 * @returns
 */
export const filterOutProjects = (project, staffing) => {
  const {
    projectsStatus,
    projectMethodology,
    projectServiceType,
    projectVenue,
    projectLocationType,
    projectCompanyId,
  } = staffing;

  if (!project) {
    return null;
  }

  // hide closed when not specifiging filiering on closed
  // This removes closed from the all view.
  if (
    projectsStatus !== PROJECT_STATUS_STATES.CLOSED &&
    project.status === PROJECT_STATUS_STATES.CLOSED
  ) {
    return null;
  }

  if (projectsStatus && project.status !== projectsStatus) {
    return null;
  }

  if (projectMethodology && project.projectMethodologyId !== projectMethodology) {
    return null;
  }

  if (projectServiceType && project.serviceType !== projectServiceType) {
    return null;
  }

  if (projectVenue && project.venueId !== projectVenue) {
    return null;
  }

  if (projectLocationType && project.locationType !== projectLocationType) {
    return null;
  }

  if (projectCompanyId && project.companyId !== projectCompanyId) {
    return null;
  }

  return project;
};
